import { createContext, useEffect, useReducer } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import Reducer from "./reducer";
import { 
    deleteDevice, 
    deleteSimNumber, 
    fetchFtm, 
    fetchFTMHolidaysCalendar, 
    fetchSkills, 
    trackerActiveAndReceiving, 
    trackerDispatch, 
    updateDevice, 
    updateLastMessageSeenUser, 
    updateSimNumber, 
    updateTrackerInstalled 
} from "./service";
import toast from "react-hot-toast";
import { commonSwal } from "../../components/swalAlert/swalAlert";
import { initialState } from "./initialState";

const Context = createContext();

const Provider = ({ children }) => {
    const { ftm_id } = useParams();
    const [state, dispatch] = useReducer(Reducer, initialState);

    useEffect(() => {
        const getData = async () => {
            dispatch({ type: 'IS_LOADING' });

            try {
                const [ftm, skills] = await Promise.all([fetchFtm(ftm_id), fetchSkills()]);
                dispatch({ type: "IS_LOADED", ftm, skills });
            } catch (error) {
                console.log(error);
            }
        };

        getData();
    }, [ftm_id, state.ftm_edit_count, state.refresh_count]);

    const setupDevice = async (ftm_device_id) => {
        commonSwal({
            notificationType: 'UPDATE_DEVICE',
            preConfirm: async (device_id) => {
                if (device_id === "" || device_id === ftm_device_id) {
                    Swal.showValidationMessage(`Invalid Device ID`);
                } else {
                    return await updateDevice(ftm_id, device_id);
                }
            },
            isConfirmed: (result) => {
                if (result.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    toast.success(result.value.message);
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                } else {
                    toast.error(result.value.message);
                }
            }
        })
    };

    const removeDevice = async () => {
        commonSwal({
            notificationType: 'REMOVE_DEVICE',
            preConfirm: () => deleteDevice(ftm_id),
            isConfirmed: (result) => {
                if (result.isConfirmed && result.value.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    toast.success(result.value.message);
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                } else {
                    toast.error(result.value.message);
                }
            }
        })
    };

    const setupSimNumber = async () => {
        commonSwal({
            notificationType: 'UPDATE_SIM_NUMBER',
            preConfirm: async (sim_number) => {
                const regex = /^\+447\d{9}$/;
                
                if (!sim_number) {
                    Swal.showValidationMessage("Sim number cannot be empty");
                    return;
                } else if (regex.test(sim_number) === false) {
                    Swal.showValidationMessage("Invalid mobile number format. It should start with +447 followed by 9 digits.");
                    return;
                } else {
                    return await updateSimNumber(ftm_id, sim_number);
                }
            },
            isConfirmed: (result) => {
                if (result.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    toast.success(result?.value?.message);
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                } else {
                    toast.error(result?.value?.message);
                }
            }
        });
    };

    const removeSimNumber = async () => {
        commonSwal({
            notificationType: 'REMOVE_SIM_NUMBER',
            preConfirm: () => deleteSimNumber(ftm_id),
            isConfirmed: (result) => {
                if (result.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    toast.success(result?.value?.message);
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                } else {
                    toast.error(result?.value?.message);
                }
            }
        })
    };

    const trackerInstalled = async () => {
        commonSwal({
            notificationType: 'FTM_TRACKER_INSTALLED',
            preConfirm: async () => {
                return await updateTrackerInstalled(ftm_id);
            },
            isConfirmed: (result) => {
                if (result.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    toast.success(result?.value?.message);
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                } else {
                    toast.error(result?.value?.message);
                }
            }
        });
    };

    const ftmTrackerDispatch = async () => {
        commonSwal({
            notificationType: 'FTM_TRACKER_DISPATCH',
            preConfirm: () => trackerDispatch(ftm_id),
            isConfirmed: (result) => {
                if (result?.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                    toast.success(result?.value?.message);
                } else {
                    toast.error(result?.value?.message);
                }
            }
        })
    };

    const ftmTrackerActiveAndReceivingLocations = async () => {
        commonSwal({
            notificationType: 'FTM_TRACKER_ACTIVE_AND_RECEIVING',
            preConfirm: () => trackerActiveAndReceiving(ftm_id),
            isConfirmed: (result) => {
                if (result?.isConfirmed && result?.value?.status === "Success") {
                    dispatch({ type: "SCROLL_IN_TO_VIEW", scroll_to_tracker_view: true });
                    dispatch({ type: "SET_FTM_EDIT_COUNT" });
                    toast.success(result?.value?.message);
                } else {
                    toast.error(result?.value?.message);
                }
            }
        })
    };

    const openSkillModal = () => dispatch({ type: "IS_SHOW_SKILL_MODAL", is_show_skill_modal: true });
    const closeSkillModal = () => dispatch({ type: "IS_SHOW_SKILL_MODAL", is_show_skill_modal: false });

    const updateRefreshCount = () => dispatch({ type: "UPDATE_REFRESH_COUNT"});

    const loadFTMHolidaysCalendar = async ({start_date, end_date}) => {
        const startTimestamp = new Date(start_date).getTime();
        const endTimestamp = new Date(end_date).getTime();
    
        const response = await fetchFTMHolidaysCalendar({ftm_id, start_date: startTimestamp, end_date: endTimestamp});
        if(response?.status === "Success"){
            dispatch({
                type: "LOAD_FTM_HOLIDAYS_CALENDAR", 
                holiday_list: response.holiday_list
            });
        }
    }

    const openChatView = async (document_id) => {
        await updateLastMessageSeenUser(document_id);
        dispatch({
            type: 'IS_SHOW_CHAT', 
            is_show_chat: true
        });
    }

    const openEditView = () => dispatch({
        type: 'IS_SHOW_EDIT_FTM', 
        is_show_edit_ftm: true
    });

    const handleIsShowEditFtm = () => dispatch({
        type: 'IS_SHOW_EDIT_FTM', 
        is_show_edit_ftm: false
    });

    useEffect(() => {
        const ftmHasDocuments = () => {
            state.ftm?.ftm_personal_documents?.forEach(doc => {
                const files = doc.document?.ftm_personal_document_files;
                if (Array.isArray(files)) {
                    if (files.length === 0) {
                        dispatch({ type: "SET_HAS_EMPTY_DOCS", has_empty_docs: true });
                    }
                }
            });
        };
        
        ftmHasDocuments();
    }, [state.ftm?.ftm_personal_documents]);

    return (
        <Context.Provider 
            value={{ 
                ftm_id, 
                state, 
                dispatch, 
                setupDevice, 
                removeDevice, 
                ftmTrackerDispatch, 
                openSkillModal, 
                closeSkillModal, 
                updateRefreshCount, 
                loadFTMHolidaysCalendar,
                openChatView,
                openEditView,
                handleIsShowEditFtm,
                ftmTrackerActiveAndReceivingLocations,
                setupSimNumber,
                removeSimNumber,
                trackerInstalled
            }}
        >
            {children}
        </Context.Provider>
    );
};

export { Context, Provider };